import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Grid,
  Card,
  CardContent,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Delete,
  Visibility,
  FilterList,
  Clear,
  LocalShipping,
  Image,
  Person,
  Store,
  AccessTime,
  Notes,
} from '@mui/icons-material';
import ProfileAvatar from '../../components/common/ProfileAvatar';
import vehicleCaptureService from '../../services/vehicleCaptureService';
import storeService from '../../services/storeService';
import { VehicleCapture, VehicleType, Store as StoreType } from '../../types';

const VehicleCapturesPage: React.FC = () => {
  const [captures, setCaptures] = useState<VehicleCapture[]>([]);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [filters, setFilters] = useState({
    store_id: '',
    vehicle_type: '' as VehicleType | '',
    start_date: '',
    end_date: '',
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    pages: 1,
  });
  const [selectedCapture, setSelectedCapture] = useState<VehicleCapture | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const vehicleTypeLabels: Record<VehicleType, string> = {
    dump_vehicle: 'Dump Vehicle',
    hbl_4: 'HBL 4',
    hbl_8: 'HBL 8',
    mrl: 'MRL',
  };

  useEffect(() => {
    fetchStores();
    fetchCaptures();
  }, []);

  useEffect(() => {
    fetchCaptures();
  }, [filters.page, filters.store_id, filters.vehicle_type, filters.start_date, filters.end_date]);

  const fetchStores = async () => {
    try {
      const response = await storeService.getStores({ limit: 1000 });
      setStores(response.stores || []);
    } catch (err: any) {
      console.error('Failed to fetch stores:', err);
    }
  };

  const fetchCaptures = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: filters.page,
        limit: filters.limit,
      };
      
      if (filters.store_id) params.store_id = filters.store_id;
      if (filters.vehicle_type) params.vehicle_type = filters.vehicle_type;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await vehicleCaptureService.getVehicleCaptures(params);
      setCaptures(response.captures || []);
      setPagination(response.pagination || { total: 0, page: 1, limit: 20, pages: 1 });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch vehicle captures');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;

    try {
      await vehicleCaptureService.deleteVehicleCapture(deletingId);
      setSuccess('Vehicle capture deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingId(null);
      fetchCaptures();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete vehicle capture');
      setDeleteDialogOpen(false);
      setDeletingId(null);
    }
  };

  const handleView = (capture: VehicleCapture) => {
    setSelectedCapture(capture);
    setViewDialogOpen(true);
  };

  const handleClearFilters = () => {
    setFilters({
      store_id: '',
      vehicle_type: '' as VehicleType | '',
      start_date: '',
      end_date: '',
      page: 1,
      limit: 20,
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Vehicle Captures
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Store</InputLabel>
            <Select
              value={filters.store_id}
              label="Store"
              onChange={(e) => setFilters({ ...filters, store_id: e.target.value, page: 1 })}
            >
              <MenuItem value="">All Stores</MenuItem>
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Vehicle Type</InputLabel>
            <Select
              value={filters.vehicle_type}
              label="Vehicle Type"
              onChange={(e) => setFilters({ ...filters, vehicle_type: e.target.value as VehicleType | '', page: 1 })}
            >
              <MenuItem value="">All Types</MenuItem>
              {Object.entries(vehicleTypeLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Start Date"
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value, page: 1 })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <TextField
            label="End Date"
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value, page: 1 })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClearFilters}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Captures List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : captures.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No vehicle captures found
          </Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {captures.map((capture) => (
              <Grid item xs={12} sm={6} md={4} key={capture.id}>
                <Card>
                  <CardContent>
                    {/* Photo */}
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <img
                        src={capture.photo_url}
                        alt="Vehicle"
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=Image+Not+Found';
                        }}
                      />
                    </Box>

                    {/* Vehicle Type */}
                    <Chip
                      label={vehicleTypeLabels[capture.vehicle_type]}
                      color="primary"
                      sx={{ mb: 1 }}
                    />

                    {/* Store */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                      <Store fontSize="small" color="action" />
                      <Typography variant="body2">
                        {capture.store?.name || 'Unknown Store'}
                      </Typography>
                    </Box>

                    {/* Captured At */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                      <AccessTime fontSize="small" color="action" />
                      <Typography variant="body2">
                        {formatDate(capture.captured_at)} at {formatTime(capture.captured_at)}
                      </Typography>
                    </Box>

                    {/* Capturer */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                      <ProfileAvatar
                        imageUrl={capture.capturer?.profile_image_url}
                        firstName={capture.capturer?.first_name}
                        lastName={capture.capturer?.last_name}
                        sx={{ width: 24, height: 24, bgcolor: '#081122' }}
                      />
                      <Typography variant="body2">
                        {capture.capturer?.first_name} {capture.capturer?.last_name}
                        {capture.capturer?.employee_id && ` (${capture.capturer.employee_id})`}
                      </Typography>
                    </Box>

                    {/* Notes */}
                    {capture.notes && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                        <Notes fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {capture.notes}
                        </Typography>
                      </Box>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleView(capture)}
                        fullWidth
                      >
                        View
                      </Button>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => {
                          setDeletingId(capture.id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} captures
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                disabled={pagination.page === 1}
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              >
                Previous
              </Button>
              <Button
                disabled={pagination.page >= pagination.pages}
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              >
                Next
              </Button>
            </Box>
          </Box>
        </>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Vehicle Capture Details</DialogTitle>
        <DialogContent>
          {selectedCapture && (
            <Box>
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <img
                  src={selectedCapture.photo_url}
                  alt="Vehicle"
                  style={{
                    width: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x400?text=Image+Not+Found';
                  }}
                />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Vehicle Type</Typography>
                  <Typography variant="body1">{vehicleTypeLabels[selectedCapture.vehicle_type]}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Store</Typography>
                  <Typography variant="body1">{selectedCapture.store?.name || 'Unknown'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Captured At</Typography>
                  <Typography variant="body1">
                    {formatDate(selectedCapture.captured_at)} at {formatTime(selectedCapture.captured_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Captured By</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <ProfileAvatar
                      imageUrl={selectedCapture.capturer?.profile_image_url}
                      firstName={selectedCapture.capturer?.first_name}
                      lastName={selectedCapture.capturer?.last_name}
                      sx={{ width: 32, height: 32, bgcolor: '#081122' }}
                    />
                    <Typography variant="body1">
                      {selectedCapture.capturer?.first_name} {selectedCapture.capturer?.last_name}
                      {selectedCapture.capturer?.employee_id && ` (${selectedCapture.capturer.employee_id})`}
                    </Typography>
                  </Box>
                </Grid>
                {selectedCapture.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">{selectedCapture.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Vehicle Capture</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this vehicle capture? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        message={success}
      />
    </Box>
  );
};

export default VehicleCapturesPage;

